import { useEffect, useRef } from 'react'

import { useCalendarDateRangeViewMode } from './useCalendarDateRangeViewMode'
import type { CalendarDateRangeViewMode } from './useCalendarDateRangeViewMode'
import { useCalendarMobileMode } from './useCalendarMobileMode'

/**
 * Wraps `useCalendarDateRangeViewMode` with responsive behavior:
 * - On mobile: auto-switches to 'three-day' when first entering mobile mode.
 * - On desktop: normalizes 'three-day' back to 'week' (three-day is mobile-only).
 * - All view modes (including three-day) are reflected in the URL.
 */
export function useResponsiveViewMode(): [
  CalendarDateRangeViewMode,
  (mode: CalendarDateRangeViewMode) => void,
  boolean,
] {
  const isMobile = useCalendarMobileMode()
  const [viewMode, setViewMode] = useCalendarDateRangeViewMode()
  const prevIsMobileRef = useRef(false)

  useEffect(() => {
    const justBecameMobile = isMobile && !prevIsMobileRef.current
    prevIsMobileRef.current = isMobile

    if (justBecameMobile && viewMode !== 'three-day') {
      setViewMode('three-day')
    } else if (!isMobile && viewMode === 'three-day') {
      setViewMode('week')
    }
  }, [isMobile, viewMode, setViewMode])

  return [viewMode, setViewMode, isMobile]
}
